import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { getToken, setToken, removeToken, getUserFromToken, isTokenExpired } from '../utils/token';
import { loginApi } from '../services/authService';
import api from '../services/api';

type UserType = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  roles?: string[];
};

type ApiError = {
  response?: {
    data?: {
      detail?: string;
    };
  };
  message?: string;
};

interface AuthContextType {
  user: UserType | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  hasRole: (role: string) => boolean;
  profileLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserType | null>(null);
  const [token, setTokenState] = useState<string | null>(getToken());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const hydrateUser = async () => {
      setProfileLoading(true);
      const token = getToken();

      if (token && !isTokenExpired(token)) {
        try {
          const response = await api.get('/users/me');
          const userData = response.data;
          const userObj = {
            id: String(userData.id),
            email: userData.email,
            first_name: userData.first_name,
            last_name: userData.last_name,
            role: userData.role,
            roles: Array.isArray(userData.roles) && userData.roles.length > 0 ? userData.roles : [userData.role],
          };

          if (!cancelled) {
            setUser(userObj);
            localStorage.setItem('user', JSON.stringify(userObj));
          }
        } catch (error) {
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser) as UserType;
              if (!cancelled) {
                setUser({
                  ...parsedUser,
                  roles: parsedUser.roles && parsedUser.roles.length > 0 ? parsedUser.roles : [parsedUser.role],
                });
              }
            } catch (parseError) {
              const userFromToken = getUserFromToken(token);
              if (userFromToken && !cancelled) {
                setUser({
                  ...(userFromToken as UserType),
                  roles: [(userFromToken as UserType).role],
                });
              }
            }
          } else {
            const userFromToken = getUserFromToken(token);
            if (userFromToken && !cancelled) {
              setUser({
                ...(userFromToken as UserType),
                roles: [(userFromToken as UserType).role],
              });
            } else {
              removeToken();
              setTokenState(null);
              if (!cancelled) {
                setUser(null);
              }
            }
          }
        }
      } else {
        if (token) {
          removeToken();
          setTokenState(null);
        }
        if (!cancelled) {
          setUser(null);
        }
      }

      if (!cancelled) {
        setProfileLoading(false);
      }
    };

    void hydrateUser();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const { access_token, user: userData } = await loginApi(email, password);
      
      setToken(access_token);
      setTokenState(access_token);

      const savedToken = getToken();
      if (!savedToken) {
        throw new Error('Token non salvato correttamente');
      }

      const userObj = {
        id: userData.id,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        role: userData.role,
        roles: Array.isArray(userData.roles) && userData.roles.length > 0 ? userData.roles : [userData.role],
      };
      setUser(userObj);
      
      localStorage.setItem('user', JSON.stringify(userObj));

      window.location.href = '/cruscotto';
    } catch (err: unknown) {
      const error = err as ApiError;
      setError(error.response?.data?.detail || (error as Error).message || 'Errore di autenticazione');
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    removeToken();
    setTokenState(null);
    setUser(null);
    localStorage.removeItem('user');
  }, []);

  const hasRole = useCallback((role: string) => {
    return user?.role === role || user?.roles?.includes(role) === true;
  }, [user?.role, user?.roles]);

  const contextValue = useMemo(() => ({
    user,
    token,
    loading,
    error,
    login,
    logout,
    hasRole,
    profileLoading
  }), [user, token, loading, error, login, logout, hasRole, profileLoading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
};
